import React, { useEffect, useMemo, useState } from "react";
import { Col } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";

const ParticipantDropdowns = ({
  attendedUsers = [],
  selectedLanguage,
  profileId,
  userCreatedThisSurvey,
  isLoggedIn,
  hasUserParticipated,
  onUserClick = () => { }
}) => {
  const [isPublicListOpen, setIsPublicListOpen] = useState(false);
  const [isOtherListOpen, setIsOtherListOpen] = useState(false);

  const translateText = (englishText, italianText) =>
    selectedLanguage?.name === "Italian" ? italianText : englishText;

  const { publicParticipants, otherParticipants, totalParticipants, currentUserPrivacy } = useMemo(() => {
    const publicList = [];
    const otherList = [];
    let userPrivacy = null;

    attendedUsers.forEach(user => {
      const privacy = user?.user_steps?.surveyPrivacy;
      if (privacy === "Public") {
        publicList.push(user);
      } else {
        otherList.push(user);
      }

      const participantId = user?._id || user?.userId;
      if (!userPrivacy && profileId && participantId === profileId) {
        userPrivacy = privacy;
      }
    });

    return {
      publicParticipants: publicList,
      otherParticipants: otherList,
      totalParticipants: attendedUsers.length,
      currentUserPrivacy: userPrivacy
    };
  }, [attendedUsers, profileId]);

  const canSeePublicList =
    userCreatedThisSurvey || (isLoggedIn && hasUserParticipated && currentUserPrivacy === "Public");
  const canSeeOtherList = userCreatedThisSurvey;
  const canToggleLists = userCreatedThisSurvey || (isLoggedIn && hasUserParticipated);

  useEffect(() => {
    if (!canSeePublicList) {
      setIsPublicListOpen(false);
    }
    if (!canSeeOtherList) {
      setIsOtherListOpen(false);
    }
  }, [canSeePublicList, canSeeOtherList]);

  if (totalParticipants === 0) {
    return null;
  }

  const showPrivacyWarning = () => {
    toast.warning(
      translateText(
        "You cannot view the participants of this survey because you did not participate as a Public user.",
        "Non puoi visualizzare i partecipanti di questo sondaggio perché non hai partecipato come utente pubblico."
      )
    );
  };

  const handlePublicToggle = () => {
    if (!canToggleLists) {
      return;
    }
    if (!canSeePublicList) {
      showPrivacyWarning();
      return;
    }
    setIsPublicListOpen(prev => !prev);
  };

  const handleOtherToggle = () => {
    if (!canSeeOtherList) {
      return;
    }
    setIsOtherListOpen(prev => !prev);
  };

  const renderParticipantCard = (user, fallbackKey) => (
    <Col lg={2} md={3} sm={4} xs={6} key={user?._id || user?.userId || fallbackKey}>
      <div
        className="participant_card"
        onClick={() => onUserClick(user)}
        style={{ cursor: 'pointer', textAlign: 'center', padding: '5px', margin: '5px 0' }}
      >
        <img
          src={
            user?.image
              ? `${process.env.REACT_APP_AWS_BUCKET_BASE_URL}${user.image}`
              : `${process.env.PUBLIC_URL}/images/no_profile.png`
          }
          alt={user?.name || "participant"}
          style={{ width: '90%', height: 'auto', borderRadius: '50%' }}
        />
        <span style={{ display: 'block', marginTop: '1px', fontWeight: 'bold', fontSize: '0.9em' }}>
          {user?.name || translateText("Anonymous", "Anonimo")}
        </span>
        <p style={{ marginTop: '3px', color: '#888', fontSize: '0.8em' }}>
          {moment(new Date(user?.createdAt)).format("DD MMM, YY")}
        </p>
      </div>
    </Col>
  );

  return (
    <div className="survey_attempt_content">
      <div className="row">
        <div className="col-12">
          <h6 className="survey_section_heading">
            {translateText(
              `Total participants: ${totalParticipants}`,
              `Partecipanti totali: ${totalParticipants}`
            )}
          </h6>
        </div>
        <div className="col-12">
          <div className="participant_dropdown_wrapper">
            <div className="participant_dropdown_box">
              <button
                type="button"
                className={`participant_dropdown_toggle ${isPublicListOpen ? "open" : ""} ${!canSeePublicList ? "disabled" : ""}`}
                onClick={handlePublicToggle}
              >
                <span>
                  {translateText("Public", "Pubblico")}: {publicParticipants.length}
                </span>
                <span className="participant_dropdown_icon">
                  {isPublicListOpen ? "▲" : "▼"}
                </span>
              </button>
              {isPublicListOpen && canSeePublicList && (
                <div className="participant_dropdown_content">
                  <div className="row">
                    {publicParticipants.map((user, index) => renderParticipantCard(user, `public-${index}`))}
                  </div>
                </div>
              )}
            </div>
            <div className="participant_dropdown_box">
              <button
                type="button"
                className={`participant_dropdown_toggle ${isOtherListOpen ? "open" : ""} ${!canSeeOtherList ? "disabled" : ""}`}
                onClick={handleOtherToggle}
              >
                <span>
                  {translateText("Other", "Altro")} ({translateText("Anonymous + private", "Anonimo + privato")}): {otherParticipants.length}
                </span>
                <span className="participant_dropdown_icon">
                  {isOtherListOpen ? "▲" : "▼"}
                </span>
              </button>
              {isOtherListOpen && canSeeOtherList && (
                <div className="participant_dropdown_content">
                  <div className="row">
                    {otherParticipants.map((user, index) => renderParticipantCard(user, `other-${index}`))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantDropdowns;

